import type { ReactNode } from 'react';
import type { ApiError } from '@aithora/shared';
import { ErrorNote } from '../ErrorNote';
import type { QueryState } from '../useQuery';
import { isDevNote, useCustomerText, useDevNotes } from './buildLabels';

/**
 * Loading: grey bars the height of the rows that will replace them, so the page does
 * not jump when the data lands. Announced to screen readers as busy.
 */
export function Skeleton({ rows = 3, label = 'Loading', className }: { rows?: number; label?: string; className?: string }) {
  return (
    <div className={`skeleton${className ? ` ${className}` : ''}`} aria-busy="true" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} style={{ width: `${92 - ((i * 17) % 40)}%` }} />
      ))}
    </div>
  );
}

/**
 * The designed empty state: a plain sentence saying what is not here and why. When the
 * block has no back-end route yet, `waitingOn` names what it waits for, so an empty
 * block is never mistaken for "all clear".
 */
export function EmptyState({ title, body, waitingOn, compact }: { title: string; body?: ReactNode; waitingOn?: string; compact?: boolean }) {
  // What it waits on (a route, a feed) is a developer note: Developer and Super Admin only.
  const dev = useDevNotes();
  const plain = useCustomerText();
  return (
    <div className={`emptystate${compact ? ' emptystate--compact' : ''}`} data-testid="empty-state">
      <span className="emptystate__title">{plain(title)}</span>
      {body && (typeof body !== 'string' || dev || !isDevNote(body)) && <span className="emptystate__body">{body}</span>}
      {waitingOn && dev && <span className="emptystate__waiting">Waiting on {waitingOn}</span>}
    </div>
  );
}

/** The error state: the server's sentence, a retry and the request id (ErrorNote). */
export function ErrorState({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  return <ErrorNote error={error} onRetry={onRetry} compact />;
}

/**
 * One block's three states in one place. Loading shows the skeleton only until the first
 * answer; a failed refresh keeps the last good data and shows the error beside it.
 */
export function QueryBlock<T>({
  q,
  rows = 3,
  isEmpty,
  empty,
  children,
}: {
  q: QueryState<T>;
  rows?: number;
  isEmpty?: (d: T) => boolean;
  empty?: ReactNode;
  children: (d: T) => ReactNode;
}) {
  if (q.loading && q.data === null && !q.error) return <Skeleton rows={rows} />;
  if (q.error && q.data === null) return <ErrorState error={q.error} onRetry={q.reload} />;
  if (q.data === null) return <Skeleton rows={rows} />;
  const body = isEmpty && isEmpty(q.data) ? empty : children(q.data);
  return (
    <>
      {body}
      {q.error && <ErrorState error={q.error} onRetry={q.reload} />}
    </>
  );
}
